// Pack 12 · Reset Password page. Reads `?token=…` from the URL, POSTs the
// new password, and (on success) auto-signs the user in so they land on
// the dashboard without another login step.
import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { KeyRound, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const missingToken = !token || token.length < 16;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      return setError("Password must be at least 8 characters.");
    }
    if (password !== confirm) {
      return setError("Passwords do not match.");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (data?.user) setUser({ ...data.user, role: "consultant" });
      toast.success("Password updated — you're signed in.");
      nav("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Reset failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (missingToken) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
        <Card className="card-elevated w-full max-w-md p-8" data-testid="reset-invalid-card">
          <div className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-4">
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">This reset link looks broken</p>
              <p className="text-sm text-muted-foreground mt-1">
                The token is missing from the URL. Request a new link and use the freshest email in your inbox.
              </p>
            </div>
          </div>
          <div className="text-center mt-6">
            <Link to="/forgot-password" className="text-accent hover:underline text-sm" data-testid="reset-request-new-link">
              Request a new reset link
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <Card className="card-elevated w-full max-w-md p-8" data-testid="reset-password-card">
        <div className="overline"><span className="accent-line" /> Almost there</div>
        <h1 className="font-display text-3xl mt-3">Choose a new password</h1>
        <p className="text-sm text-muted-foreground mt-4">
          Pick something at least 8 characters. You&rsquo;ll be signed in automatically once you save.
        </p>
        <form className="mt-6 space-y-4" onSubmit={submit}>
          <div>
            <Label htmlFor="new-pw">New password</Label>
            <Input
              id="new-pw"
              data-testid="reset-password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="confirm-pw">Confirm password</Label>
            <Input
              id="confirm-pw"
              data-testid="reset-password-confirm"
              type="password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••"
              className="mt-2"
            />
          </div>
          {error && (
            <div className="text-sm text-destructive" data-testid="reset-error">{error}</div>
          )}
          <Button
            type="submit"
            data-testid="reset-submit"
            disabled={busy}
            className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
          >
            <KeyRound className="w-4 h-4 mr-2" />
            {busy ? "Updating…" : "Update password & sign in"}
          </Button>
        </form>
        <p className="text-sm text-muted-foreground mt-6 text-center">
          <Link to="/login" className="text-accent hover:underline" data-testid="reset-back-link">Back to sign in</Link>
        </p>
      </Card>
    </div>
  );
}
